/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';
import type { CredentialFieldSchema, RegionSchema } from './fleetmgmtplugins';

/**
 * Per-owner booking-plugin configuration. Storage lives in
 * OwnerSettings.Settings.bookingPlugins.{platformId} JSON sections (backend).
 *
 * Two plugin families are aggregated by the backend:
 *  - family="fleetmgmt", authMode="CredentialFields" (HQ Rental, Rent Centric, RENTALL, ...)
 *  - family="rental",    authMode="ChallengeFlow"     (Turo, future Getaround/HyreCar, ...)
 *
 * The frontend renders one card type per `authMode`.
 */

export type OwnerBookingPluginFamily = 'fleetmgmt' | 'rental';
export type OwnerBookingPluginAuthMode = 'CredentialFields' | 'ChallengeFlow';

export interface OwnerBookingPluginBase {
  family: OwnerBookingPluginFamily;
  authMode: OwnerBookingPluginAuthMode;
  platformId: string;
  platformName: string;
  supportedRegions: string[];
  credentialFields: CredentialFieldSchema[];
  isConfigured: boolean;
  /** Per-secret-key flag: true if a non-empty value is stored. Never echoes the value itself. */
  secretsPresent: Record<string, boolean>;
  /** Whether the UI should show a manual "Get bookings from {platform}" button.
   *  False for plugins where bookings arrive only via background sync / email events (e.g. Turo). */
  supportsManualBookingFetch: boolean;
}

export interface OwnerBookingPluginFleet extends OwnerBookingPluginBase {
  family: 'fleetmgmt';
  authMode: 'CredentialFields';
  supportsBookingSync: boolean;
  supportsFleetSync: boolean;
  supportsCustomerSync: boolean;
  supportsWebhooks: boolean;
  supportsBookingCreate: boolean;
  regions: RegionSchema[];
  region?: string;
  tenantId?: string;
  /** How often the background poller syncs this owner, in minutes (null = global default). */
  pollIntervalMinutes?: number | null;
  /** Whether the poller also syncs vehicles (true) or bookings-only (false). null = default (true). */
  syncVehicles?: boolean | null;
}

export interface OwnerBookingPluginRental extends OwnerBookingPluginBase {
  family: 'rental';
  authMode: 'ChallengeFlow';
  supportsPasswordLogin: boolean;
  supportsEmailOtp: boolean;
  supportsSmsOtp: boolean;
  requiresCaptcha: boolean;
  emailNotificationDomains: string[];
  /** True when refresh_token is stored (ready for API calls). */
  connected: boolean;
  /** True when the refresh_token is dead (invalid_grant) → owner must reconnect (re-harvest).
   *  Distinct from a transient WAF/proxy failure, which leaves this false. */
  needsReauth?: boolean;
  /** Owner's saved login (echoed for display — not a secret). */
  login?: string | null;
  accessTokenExpiresAt?: string | null;
}

export type OwnerBookingPlugin = OwnerBookingPluginFleet | OwnerBookingPluginRental;

/** A background re-read of the platform's emails (one per owner + platform at a time). */
export interface RereadStatus {
  running: boolean;
  total: number;
  done: number;
  startedAtUtc?: string | null;
  finishedAtUtc?: string | null;
}

export interface SetConfigRequest {
  secrets: Record<string, string>;
  region?: string;
  tenantId?: string;
  /** Per-owner poller cadence (minutes). Omit to keep existing / use global default. */
  pollIntervalMinutes?: number;
  /** Whether the poller also syncs vehicles (true) or bookings-only (false). */
  syncVehicles?: boolean;
}

export interface VerifyResponse {
  success: boolean;
  accountInfoSummary?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface WebhookUrlResponse {
  /** Full URL including the per-owner secret segment. */
  url: string;
  /** False when the connected plugin does not parse webhooks — the URL would receive nothing useful. */
  supportsWebhooks: boolean;
}

export interface SyncResult {
  created: number;
  updated: number;
  skipped: number;
  error?: string;
}

/** One connected account on a multi-account rental plugin (Turo). */
export interface RentalAccount {
  accountKey: string;
  login: string;
  isDefault: boolean;
  connected: boolean;
}

export interface RentalAccountsResponse {
  supportsMultipleAccounts: boolean;
  accounts: RentalAccount[];
}

export type RentalAuthOutcomeKind =
  | 'Success' | 'EmailOtpRequired' | 'SmsOtpRequired'
  | 'SecurityChallengeRequired' | 'CaptchaRequired' | 'Failed';

export interface RentalAuthResult {
  kind: RentalAuthOutcomeKind;
  challengeToken?: string | null;
  error?: string | null;
  message?: string | null;
}

export type CollectorValue = 'off' | 'plugin' | 'platform';
export type CollectorCategory = 'booking' | 'toll' | 'violation';

/** Effective + raw stored collector for one category. */
export interface CollectorCategoryInfo {
  effective: CollectorValue;
  stored?: string | null;
}

/** Who collects payments for this owner — chosen INDEPENDENTLY per category. */
export interface PaymentCollectorInfo {
  /** True when a booking plugin is connected (only then can the owner choose). */
  hasPlugin: boolean;
  /** True when the owner is allowed to choose (hasPlugin AND not locked). */
  canChoose: boolean;
  /** True when the connected plugin locks the collector to Aegis (Turo) — the switches are read-only. */
  locked?: boolean;
  /** True when the connected plugin can collect through its OWN gateway (self-billing, e.g. HQ). When false
   *  (no plugin, or a gateway-less plugin like Renteon) the "Booking plugin" option is not selectable anywhere. */
  pluginCollects?: boolean;
  /** True when a self-billing plugin (e.g. HQ) is connected: the rental is billed by the plugin's own system,
   *  so the Booking row is locked to "off" (the other options are not selectable). A gateway-less plugin
   *  (e.g. Renteon) leaves this false — Aegis collects the booking and the Booking row stays choosable. */
  bookingForcedOff?: boolean;
  /** Per-category effective + stored collector. */
  collectors?: { booking: CollectorCategoryInfo; toll: CollectorCategoryInfo; violation: CollectorCategoryInfo };
  /** Back-compat: the booking collector (headline). */
  collector: CollectorValue;
  stored?: string | null;
}

/** A car that's mapped to the owner's booking plugin but NOT bookable through it (needs a fix). */
export interface VehicleReadinessItem {
  carId: string;
  isReady: boolean;
  issues: string[];
  fixHint?: string;
}
export interface VehicleReadinessResponse {
  platformId: string | null;
  items: VehicleReadinessItem[];
}

export const ownerBookingPluginsApi = {
  async list(ownerId: string): Promise<OwnerBookingPlugin[]> {
    const res = await apiClient.get<OwnerBookingPlugin[]>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins`);
    return res.data;
  },

  /**
   * Re-read the owner's stored notification emails from this rental platform (e.g. Turo) into bookings — for mail
   * that arrived before the plugin was enabled. Idempotent server-side. Returns how many emails were re-read.
   */
  async rereadEmails(ownerId: string, platformId: string, days = 30): Promise<{ emails: number; days: number }> {
    const res = await apiClient.post<{ emails: number; days: number }>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/reread-emails?days=${days}`);
    return res.data;
  },

  /** Where this owner's background re-read stands — drives the progress strip and keeps the button disabled. */
  async rereadEmailsStatus(ownerId: string, platformId: string): Promise<RereadStatus> {
    const res = await apiClient.get<RereadStatus>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/reread-emails/status`);
    return res.data ?? { running: false, total: 0, done: 0 };
  },

  /** Cars mapped to the booking plugin that are NOT bookable through it (owner-web "needs fix" badge). */
  async getVehicleReadiness(ownerId: string): Promise<VehicleReadinessResponse> {
    try {
      const res = await apiClient.get<VehicleReadinessResponse>(
        `/owners/${encodeURIComponent(ownerId)}/booking-plugins/vehicle-readiness`);
      return res.data ?? { platformId: null, items: [] };
    } catch {
      return { platformId: null, items: [] };
    }
  },

  /** Read the payment-collector setting (who charges: plugin gateway vs our Stripe). */
  async getPaymentCollector(ownerId: string): Promise<PaymentCollectorInfo> {
    const res = await apiClient.get<PaymentCollectorInfo>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/payment-collector`);
    return res.data;
  },

  /** Switch who collects payments for a category (default "booking"). "plugin" requires a connected plugin. */
  async setPaymentCollector(ownerId: string, collector: CollectorValue, category: CollectorCategory = 'booking'): Promise<void> {
    await apiClient.put(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/payment-collector`, { collector, category });
  },

  async set(ownerId: string, platformId: string, body: SetConfigRequest): Promise<void> {
    await apiClient.put(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}`,
      body);
  },

  async delete(ownerId: string, platformId: string): Promise<void> {
    await apiClient.delete(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}`);
  },

  async verify(ownerId: string, platformId: string): Promise<VerifyResponse> {
    const res = await apiClient.post<VerifyResponse>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/verify`);
    return res.data;
  },

  // ── Sync (re-read from the platform API) ──

  /** FleetMgmt: pull vehicles → customers → bookings in one shot (real entities). */
  async syncAll(ownerId: string, platformId: string): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/all`);
    return res.data;
  },

  /** Pull trips and create real Bookings directly (auto-confirmed). */
  async syncBookings(ownerId: string, platformId: string): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/bookings`);
    return res.data;
  },

  /** Pull trips into the OwnerPendingBookings review queue (owner confirms each). */
  async syncBookingsToPending(ownerId: string, platformId: string): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/bookings-to-pending`);
    return res.data;
  },

  /**
   * The inbound webhook URL this owner pastes into the platform's own settings. The secret segment is
   * issued on the first read and stays put afterwards; `rotate` mints a new one, which INVALIDATES the
   * URL already registered on the platform until the owner updates it there.
   */
  async getWebhookUrl(ownerId: string, platformId: string, rotate = false): Promise<WebhookUrlResponse> {
    const res = await apiClient.get<WebhookUrlResponse>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/webhook-url`
      + (rotate ? '?rotate=true' : ''));
    return res.data;
  },

  /** Pull the owner's vehicles and upsert them into Cars. */
  async syncVehicles(ownerId: string, platformId: string): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/vehicles`);
    return res.data;
  },

  /** Pull guests/drivers, parse + re-host their licence photo, store selfie as avatar. */
  async syncDrivers(ownerId: string, platformId: string): Promise<SyncResult> {
    const res = await apiClient.post<SyncResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/drivers`);
    return res.data;
  },

  /** Pull a car's rental price from the plugin and store it on the car. */
  async pullVehiclePrice(ownerId: string, platformId: string, carId: string): Promise<{ success: boolean; dailyRate?: number; message?: string }> {
    const res = await apiClient.post<{ success: boolean; dailyRate?: number; message?: string }>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/cars/${encodeURIComponent(carId)}/pull-price`);
    return res.data;
  },

  /** Write a car's rental price to the plugin (sets the rate for its vehicle class). */
  async pushVehiclePrice(ownerId: string, platformId: string, carId: string): Promise<{ success: boolean; message?: string }> {
    const res = await apiClient.post<{ success: boolean; message?: string }>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/sync/cars/${encodeURIComponent(carId)}/push-price`);
    return res.data;
  },

  // ── Multi-account (rental plugins that support several accounts, e.g. Turo) ──

  async listAccounts(ownerId: string, platformId: string): Promise<RentalAccountsResponse> {
    const res = await apiClient.get<RentalAccountsResponse>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/accounts`);
    return res.data;
  },

  async removeAccount(ownerId: string, platformId: string, accountKey: string): Promise<void> {
    await apiClient.delete(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/accounts/${encodeURIComponent(accountKey)}`);
  },

  // ── ChallengeFlow (rental plugins: Turo etc.) ──

  // accountKey (optional) connects an ADDITIONAL account on a multi-account plugin (Turo: the login email
  // of the extra account). Omit for the first / single account. The host treats it as opaque.
  async requestEmailOtp(ownerId: string, platformId: string, accountKey?: string): Promise<RentalAuthResult> {
    const res = await apiClient.post<RentalAuthResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/auth/request-email-otp${accountKey ? `?accountKey=${encodeURIComponent(accountKey)}` : ''}`);
    return res.data;
  },

  async verifyEmailOtp(ownerId: string, platformId: string, challengeToken: string, code: string, accountKey?: string): Promise<RentalAuthResult> {
    const res = await apiClient.post<RentalAuthResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/auth/verify-email-otp${accountKey ? `?accountKey=${encodeURIComponent(accountKey)}` : ''}`,
      { challengeToken, code });
    return res.data;
  },

  async loginPassword(ownerId: string, platformId: string, accountKey?: string): Promise<RentalAuthResult> {
    const res = await apiClient.post<RentalAuthResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/auth/login-password${accountKey ? `?accountKey=${encodeURIComponent(accountKey)}` : ''}`);
    return res.data;
  },

  async submitSecurityChallenge(ownerId: string, platformId: string, challengeToken: string, code: string, accountKey?: string): Promise<RentalAuthResult> {
    const res = await apiClient.post<RentalAuthResult>(
      `/owners/${encodeURIComponent(ownerId)}/booking-plugins/${encodeURIComponent(platformId)}/auth/submit-challenge`,
      { challengeToken, code, accountKey });
    return res.data;
  },
};
